import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  ChangeDetectorRef,
} from '@angular/core';
import { DetailsCardComponent } from '../../../components/details-card/details-card.component';
import {
  QaiLinkButtonComponent,
  QaiWordListComponent,
  WordItem,
} from '@qai/ui';
import { ActivatedRoute } from '@angular/router';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { CallWordType } from 'src/app/types/call-word.types';
import { FormGroup, FormArray, FormControl, FormBuilder } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { AuthorReactiveFormService } from '../../../services/author-reactive-form.service';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import {
  take,
  debounceTime,
  distinctUntilChanged,
  Subscription,
  skip,
  switchMap,
  of,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { IWordDefinition } from 'src/app/interfaces/self-serve/responses/iword-definition';
import { IWordsRequest } from 'src/app/interfaces/self-serve/requests/iwords-request';
import { IGetWordDetailsResponse } from 'src/app/interfaces/self-serve/responses/iget-word-details-response';
import { SaveAsProductDefaultModalComponent } from '../../../components/modals/save-as-product-default-modal/save-as-product-default-modal.component';

@Component({
  selector: 'app-clean-transcript-words',
  imports: [
    DetailsCardComponent,
    QaiLinkButtonComponent,
    QaiWordListComponent,
    CommonModule,
    ReactiveFormsModule,
    SaveAsProductDefaultModalComponent,
  ],
  templateUrl: './clean-transcript-words.component.html',
  styleUrl: './clean-transcript-words.component.scss',
})
export class CleanTranscriptWordsComponent implements OnInit, OnDestroy {
  title: string = 'Clean transcript words';
  description: string =
    'Words that are injected to the CLEAN_TRANSCRIPT_V2 prompt.';
  buttonText: string = 'Add new word';
  callUuid: string = '';
  moduleUuid: string = '';
  personaUuid: string = '';
  form!: FormGroup;
  cleanTranscriptWordsListControl: FormControl<WordItem[]> = new FormControl<
    WordItem[]
  >([], {
    nonNullable: true,
  });
  private cleanTranscriptWordsSubscription?: Subscription;
  private cleanTranscriptWordsListControlSubscription?: Subscription;
  private isUpdatingFromFormArray = signal(false);
  private isUpdatingFromAPI = signal(false);
  private wordTypeUuid: string = '';
  private apiErrors = new Map<string, string>();
  private duplicateWords = new Set<string>();
  private readonly ERROR_DUPLICATE_WORD = 'Duplicate words are not allowed';
  private readonly DUPLICATE_WORD_STATUS = 409;
  saveAsProductDefaultModalOpen: boolean = false;
  keywordTitle: string = 'clean transcript words';
  networkRequestInProgress: boolean = false;
  private apiRequestCount = signal(0);
  readonly isApiRequestInProgress = computed(() => this.apiRequestCount() > 0);

  constructor(
    private authorService: AuthorService,
    private route: ActivatedRoute,
    private authorReactiveFormService: AuthorReactiveFormService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {}

  get cleanTranscriptWordsFormArray(): FormArray {
    return this.form?.get(
      SimulationConstants.CLEAN_TRANSCRIPT_WORDS_SECTION
    ) as FormArray;
  }

  get hasNoWords(): boolean {
    const wordItems = this.cleanTranscriptWordsListControl?.value || [];
    return (
      wordItems.length === 0 ||
      wordItems.every((item) => !item.value || item.value.trim().length === 0)
    );
  }

  get hasErrors(): boolean {
    return this.apiErrors.size > 0;
  }

  get allWordsHaveProductScope(): boolean {
    if (!this.cleanTranscriptWordsFormArray || this.hasNoWords) {
      return false;
    }
    const words =
      this.cleanTranscriptWordsFormArray.getRawValue() as IWordDefinition[];
    if (words.length === 0) {
      return false;
    }
    return words.every(
      (word) => word?.scope === SimulationConstants.KEYWORDS_PRODUCT_SCOPE
    );
  }

  get hasProductScopedWords(): boolean {
    const hasProductScopedWordsControl = this.form?.get(
      'clean_transcript_words_hasProductScopedWords'
    );
    return hasProductScopedWordsControl?.value ?? false;
  }

  getFieldError = (
    item: WordItem,
    index: number,
    field: 'value'
  ): string | undefined => {
    const trimmedValue = item.value?.trim() || '';
    const itemId = item.uuid || trimmedValue || `index-${index}`;
    const apiError = this.apiErrors.get(itemId);

    if (apiError) {
      return apiError;
    }

    return undefined;
  };

  private updateFormControlDisabledState(): void {
    const shouldDisable = !this.hasNoWords && this.allWordsHaveProductScope;
    const wasDisabled = this.cleanTranscriptWordsListControl.disabled;
    
    if (shouldDisable) {
      if (!wasDisabled) {
        this.cleanTranscriptWordsListControl.disable({ emitEvent: false });
      }
    } else {
      if (wasDisabled) {
        const formArrayWords =
          (this.cleanTranscriptWordsFormArray?.getRawValue() as IWordDefinition[]) ||
          [];
        const expectedWordItems: WordItem[] = formArrayWords
          .filter((word) => word?.key?.trim())
          .map((word) => ({
            value: word.key || '',
            uuid: word.definitionUuid || undefined,
          }));

        if (expectedWordItems.length > 0) {
          this.isUpdatingFromFormArray.set(true);
          this.cleanTranscriptWordsListControl.setValue(expectedWordItems, {
            emitEvent: false,
          });
          this.isUpdatingFromFormArray.set(false);
        }

        this.cleanTranscriptWordsListControl.enable({ emitEvent: false });
        
        if (expectedWordItems.length > 0) {
          this.isUpdatingFromFormArray.set(true);
          this.cleanTranscriptWordsListControl.setValue(expectedWordItems, {
            emitEvent: false,
          });
          this.isUpdatingFromFormArray.set(false);
        }
      }
    }
  }

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid') || '';
    this.loadPersonaUuid();
    this.loadModuleUuid();
    this.loadCleanTranscriptWords();
  }

  private loadCleanTranscriptWords(): void {
    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form) => {
        this.form = form.get(SimulationConstants.KEYWORDS_SECTION) as FormGroup;

        const wordTypeUuidControl = this.form?.get(
          'clean_transcript_words_wordTypeUuid'
        );
        if (wordTypeUuidControl) {
          this.wordTypeUuid = wordTypeUuidControl.value || '';
        }

        if (this.cleanTranscriptWordsFormArray) {
          this.syncFormArrayToControl();
          this.setupSyncBetweenFormArrayAndControl();
          this.updateFormControlDisabledState();
        }
      });
  }

  private extractKeysFromFormArray(): string[] {
    if (!this.cleanTranscriptWordsFormArray) return [];
    return this.authorReactiveFormService.extractKeysFromFormArray(
      this.cleanTranscriptWordsFormArray
    );
  }

  private syncFormArrayToControl(): void {
    if (!this.cleanTranscriptWordsFormArray) return;

    this.isUpdatingFromFormArray.set(true);
    const words =
      this.cleanTranscriptWordsFormArray.getRawValue() as IWordDefinition[];
    const wordItems: WordItem[] = words.map((word) => ({
      value: word.key || '',
      uuid: word.definitionUuid || undefined,
    }));

    this.cleanTranscriptWordsListControl.setValue(wordItems, {
      emitEvent: false,
    });
    this.isUpdatingFromFormArray.set(false);
  }

  private syncWordListToFormArray(
    wordItems: WordItem[],
    skipApiProcessing: boolean = false
  ): void {
    if (!this.cleanTranscriptWordsFormArray) return;
    this.authorReactiveFormService.syncWordItemsToFormArray(
      this.cleanTranscriptWordsFormArray,
      wordItems,
      SimulationConstants.KEYWORDS_DEFAULT_SCOPE,
      SimulationConstants.CLEAN_TRANSCRIPT_WORDS_SECTION,
      this.isUpdatingFromFormArray
    );

    this.cdr.detectChanges();

    if (!skipApiProcessing) {
      requestAnimationFrame(() => {
        if (!this.isUpdatingFromAPI()) {
          this.processCleanTranscriptWordsForAPI();
        }
      });
    }
  }

  private setupSyncBetweenFormArrayAndControl(): void {
    if (this.cleanTranscriptWordsSubscription) {
      this.cleanTranscriptWordsSubscription.unsubscribe();
    }
    if (this.cleanTranscriptWordsListControlSubscription) {
      this.cleanTranscriptWordsListControlSubscription.unsubscribe();
    }

    this.cleanTranscriptWordsSubscription =
      this.cleanTranscriptWordsFormArray.valueChanges
        .pipe(
          skip(1),
          debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
          distinctUntilChanged(
            (a, b) => JSON.stringify(a) === JSON.stringify(b)
          )
        )
        .subscribe(() => {
          if (!this.cleanTranscriptWordsFormArray) return;

          if (this.isUpdatingFromAPI()) {
            return;
          }

          if (this.isUpdatingFromFormArray()) {
            return;
          }

          this.personaUuid = this.getPersonaUuidFromForm();

          this.syncFormArrayToControl();
          this.updateFormControlDisabledState();
          this.processCleanTranscriptWordsForAPI();
        });

    this.cleanTranscriptWordsListControlSubscription =
      this.cleanTranscriptWordsListControl.valueChanges
        .pipe(
          skip(1),
          debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
          distinctUntilChanged(
            (a, b) => JSON.stringify(a) === JSON.stringify(b)
          )
        )
        .subscribe((wordItems: WordItem[]) => {
          if (this.isUpdatingFromAPI()) {
            return;
          }

          if (this.isUpdatingFromFormArray()) {
            return;
          }

          if (wordItems.length === 1 && (!wordItems[0].value || wordItems[0].value.trim().length === 0)) {
            const formArrayWords =
              (this.cleanTranscriptWordsFormArray?.getRawValue() as IWordDefinition[]) || [];
            const hasApiWords = formArrayWords.some((word) => word?.definitionUuid);
            if (hasApiWords) {
              this.syncFormArrayToControl();
            }
            return;
          }

          if (this.apiErrors.size > 0) {
            this.clearErrorsForModifiedWords(wordItems);
            this.syncWordListToFormArray(wordItems, true);
            this.validateAndUpdateErrors();

            if (this.apiErrors.size === 0) {
              this.cleanTranscriptWordsListControl.updateValueAndValidity();
              this.updateFormControlDisabledState();
            } else {
              return;
            }
          }

          const hasIncompleteFields = wordItems.some(
            (item) => !item.value || item.value.trim().length === 0
          );

          if (hasIncompleteFields) {
            this.syncWordListToFormArray(wordItems);
            this.updateFormControlDisabledState();
            this.syncWordListToFormArray(wordItems, true);
            return;
          }

          this.syncWordListToFormArray(wordItems);
          this.updateFormControlDisabledState();
        });
  }

  private getPersonaUuidFromForm(): string {
    if (!this.form) return '';
    const generalSection = this.form.parent?.get(
      SimulationConstants.GENERAL_SECTION
    ) as FormGroup;
    const callPersonaGroup = generalSection?.get(
      SimulationConstants.CALL_PERSONA_SECTION
    ) as FormGroup;
    const personaUuidControl = callPersonaGroup?.get('uuid');
    return personaUuidControl?.value || '';
  }

  private getScopeUuid(scope: string): string {
    switch (scope) {
      case SimulationConstants.KEYWORDS_PRODUCT_SCOPE:
        return this.moduleUuid;
      case SimulationConstants.KEYWORDS_PERSONA_SCOPE:
        return this.getPersonaUuidFromForm();
      case SimulationConstants.KEYWORDS_DEFAULT_SCOPE:
      default:
        return this.callUuid;
    }
  }

  private transformWordsToRequest(
    words: IWordDefinition[],
    includeUuid: boolean = false
  ): IWordsRequest {
    const validWords = words.filter(
      (word) => word.key && word.key.trim().length > 0
    );

    const filteredWords = includeUuid
      ? validWords.filter(
          (word) => word.definitionUuid && word.definitionUuid.trim().length > 0
        )
      : validWords;

    let wordTypeUuidToUse = this.wordTypeUuid;
    if (!wordTypeUuidToUse || wordTypeUuidToUse.trim().length === 0) {
      const wordTypeUuidControl = this.form?.get(
        'clean_transcript_words_wordTypeUuid'
      );
      wordTypeUuidToUse = wordTypeUuidControl?.value || '';
    }
    if (!wordTypeUuidToUse || wordTypeUuidToUse.trim().length === 0) {
      wordTypeUuidToUse =
        filteredWords.find(
          (word) => word.wordTypeUuid && word.wordTypeUuid.trim().length > 0
        )?.wordTypeUuid || '';
    }

    if (!wordTypeUuidToUse || wordTypeUuidToUse.trim().length === 0) {
      return { wordTypeEntries: [] };
    }

    const wordsWithTypeUuid = filteredWords.map((word) => ({
      ...word,
      wordTypeUuid: word.wordTypeUuid || wordTypeUuidToUse,
    }));

    const wordsByType = new Map<string, IWordDefinition[]>();
    wordsWithTypeUuid.forEach((word) => {
      const wordTypeUuid = word.wordTypeUuid!.trim();
      if (!wordsByType.has(wordTypeUuid)) {
        wordsByType.set(wordTypeUuid, []);
      }
      wordsByType.get(wordTypeUuid)!.push(word);
    });

    const wordTypeEntries = Array.from(wordsByType.entries()).map(
      ([wordTypeUuid, typeWords]) => {
        const firstWord = typeWords[0];
        const scope =
          firstWord.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE;

        return {
          wordTypeUuid: wordTypeUuid,
          scope,
          scopeUuid: this.getScopeUuid(scope),
          words: typeWords.map((word) => {
            const entry: {
              uuid?: string;
              wordKey: string;
              wordValue?: string | null;
            } = {
              wordKey: word.key.trim(),
            };

            if (includeUuid && word.definitionUuid) {
              entry.uuid = word.definitionUuid;
            }

            if (word.value) {
              entry.wordValue = word.value;
            }

            return entry;
          }),
        };
      }
    );

    return { wordTypeEntries };
  }

  private updateFormWithWordDetails(
    wordDetails: IGetWordDetailsResponse,
    forceReplace: boolean = false
  ): void {
    if (!wordDetails || !Array.isArray(wordDetails)) {
      return;
    }

    const cleanTranscriptWordsGroup = wordDetails.find(
      (group) =>
        group.wordTypeName ===
        SimulationConstants.CLEAN_TRANSCRIPT_WORDS_SECTION
    );

    if (!cleanTranscriptWordsGroup) {
      return;
    }

    if (cleanTranscriptWordsGroup.wordTypeUuid) {
      this.wordTypeUuid = cleanTranscriptWordsGroup.wordTypeUuid;
      const wordTypeUuidControl = this.form?.get(
        'clean_transcript_words_wordTypeUuid'
      );
      if (wordTypeUuidControl) {
        wordTypeUuidControl.setValue(cleanTranscriptWordsGroup.wordTypeUuid, {
          emitEvent: false,
        });
      }
    }

    if (cleanTranscriptWordsGroup.hasProductScopedWords !== undefined) {
      const hasProductScopedWordsControl = this.form?.get(
        'clean_transcript_words_hasProductScopedWords'
      );
      if (hasProductScopedWordsControl) {
        hasProductScopedWordsControl.setValue(
          cleanTranscriptWordsGroup.hasProductScopedWords,
          { emitEvent: false }
        );
      }
    }

    if (
      !cleanTranscriptWordsGroup.words ||
      cleanTranscriptWordsGroup.words.length === 0
    ) {
      this.isUpdatingFromAPI.set(true);

      if (this.cleanTranscriptWordsFormArray) {
        while (this.cleanTranscriptWordsFormArray.length > 0) {
          this.cleanTranscriptWordsFormArray.removeAt(0, { emitEvent: false });
        }
      }

      this.cleanTranscriptWordsListControl.setValue([], { emitEvent: false });
      this.isUpdatingFromAPI.set(false);
      return;
    }

    this.isUpdatingFromAPI.set(true);

    if (!this.cleanTranscriptWordsFormArray) {
      this.isUpdatingFromAPI.set(false);
      return;
    }

    const apiWords: IWordDefinition[] = cleanTranscriptWordsGroup.words.map(
      (word) => ({
        key: word.wordKey,
        value: word.wordValue,
        scope: word.scope,
        typeName: cleanTranscriptWordsGroup.wordTypeName,
        definitionUuid: word.uuid,
        wordTypeUuid: cleanTranscriptWordsGroup.wordTypeUuid,
        scopeUuid: word.scopeUuid,
      })
    );

    if (forceReplace) {
      this.cleanTranscriptWordsFormArray.clear({ emitEvent: false });
      apiWords.forEach((word) => {
        this.cleanTranscriptWordsFormArray.push(
          this.fb.group({
            key: [word.key],
            value: [word.value],
            scope: [word.scope],
            typeName: [word.typeName],
            definitionUuid: [word.definitionUuid],
            wordTypeUuid: [word.wordTypeUuid],
            scopeUuid: [
              word.scopeUuid ||
                this.getScopeUuid(
                  word.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE
                ),
            ],
          }),
          { emitEvent: false }
        );
      });

      this.syncFormArrayToControl();

      this.cdr.detectChanges();
      requestAnimationFrame(() => {
        this.updateFormControlDisabledState();
        this.isUpdatingFromAPI.set(false);
      });
      return;
    }

    if (
      this.cleanTranscriptWordsListControl &&
      this.cleanTranscriptWordsFormArray
    ) {
      const currentWordItems = this.cleanTranscriptWordsListControl.value || [];
      this.syncWordListToFormArray(currentWordItems, true);
    }

    const currentFormWords =
      this.cleanTranscriptWordsFormArray.getRawValue() as IWordDefinition[];
    const incompleteFields = currentFormWords.filter(
      (w) => !w.key?.trim() || !w.key
    );
    const apiWordsByUuid = new Map(
      apiWords
        .filter((w) => w.definitionUuid)
        .map((w) => [w.definitionUuid!, w])
    );
    const apiWordsByKey = new Map(
      apiWords.filter((w) => w.key?.trim()).map((w) => [w.key!.trim(), w])
    );
    const matchedApiWords = new Set<string>();

    for (let i = 0; i < this.cleanTranscriptWordsFormArray.length; i++) {
      const formGroup = this.cleanTranscriptWordsFormArray.at(i) as FormGroup;
      const word = formGroup.getRawValue() as IWordDefinition;
      const k = word.key?.trim() || '';
      const matched =
        word.definitionUuid && apiWordsByUuid.has(word.definitionUuid)
          ? apiWordsByUuid.get(word.definitionUuid)
          : k && apiWordsByKey.has(k)
          ? apiWordsByKey.get(k)
          : undefined;

      if (matched) {
        if (word.definitionUuid) matchedApiWords.add(word.definitionUuid);
        if (k) matchedApiWords.add(k);

        const fieldsToUpdate = [
          'definitionUuid',
          'wordTypeUuid',
          'scopeUuid',
          'scope',
          'typeName',
        ];

        if (word.definitionUuid && matched.key) {
          fieldsToUpdate.push('key');
        }

        fieldsToUpdate.forEach((field) => {
          const control = formGroup.get(field);
          if (control && matched[field as keyof IWordDefinition]) {
            control.setValue(matched[field as keyof IWordDefinition], {
              emitEvent: false,
            });
          }
        });
      }
    }

    const existing = new Set(
      (this.cleanTranscriptWordsFormArray.getRawValue() as IWordDefinition[])
        .map((w) => w.key?.trim())
        .filter((k): k is string => !!k)
    );
    const apiKeys = new Set(apiWords.map((w) => w.key?.trim()).filter(Boolean));

    apiWords.forEach((word) => {
      const k = word.key?.trim() || '';
      if (!k) return;
      if (word.definitionUuid && matchedApiWords.has(word.definitionUuid))
        return;
      if (matchedApiWords.has(k)) return;
      if (existing.has(k)) return;
      this.cleanTranscriptWordsFormArray.push(
        this.fb.group({
          key: [word.key],
          value: [word.value],
          scope: [word.scope],
          typeName: [word.typeName],
          definitionUuid: [word.definitionUuid],
          wordTypeUuid: [word.wordTypeUuid],
          scopeUuid: [
            word.scopeUuid ||
              this.getScopeUuid(
                word.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE
              ),
          ],
        }),
        { emitEvent: false }
      );
      existing.add(k);
    });

    const currentFormWordsAfterApiAdditions =
      this.cleanTranscriptWordsFormArray.getRawValue() as IWordDefinition[];
    const existingAfterAdditions = new Set(
      currentFormWordsAfterApiAdditions.map((w) => w.key?.trim() || '')
    );

    incompleteFields.forEach((word) => {
      const k = word.key?.trim() || '';
      if (k && apiKeys.has(k)) return;

      const wordKey = word.key || '';
      const alreadyExists = currentFormWordsAfterApiAdditions.some(
        (w) => (w.key || '') === wordKey && !w.key?.trim()
      );

      if (alreadyExists) return;

      this.cleanTranscriptWordsFormArray.push(
        this.fb.group({
          key: [word.key || ''],
          value: [word.value || null],
          scope: [word.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE],
          typeName: [word.typeName || cleanTranscriptWordsGroup.wordTypeName],
          definitionUuid: [word.definitionUuid || ''],
          wordTypeUuid: [
            word.wordTypeUuid || cleanTranscriptWordsGroup.wordTypeUuid,
          ],
          scopeUuid: [
            word.scopeUuid ||
              this.getScopeUuid(
                word.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE
              ),
          ],
        }),
        { emitEvent: false }
      );
    });

    this.syncFormArrayToControl();
    this.updateFormControlDisabledState();

    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      this.isUpdatingFromAPI.set(false);
    });
  }

  private createKeyword(words: IWordDefinition[]): void {
    if (!this.callUuid || words.length === 0) {
      return;
    }

    const payload = this.transformWordsToRequest(words, false);

    if (payload.wordTypeEntries.length === 0) {
      console.warn('No valid word type entries to create');
      return;
    }

    this.apiRequestCount.update((count) => count + 1);
    this.authorService
      .createWords(payload)
      .pipe(
        switchMap(() => {
          return this.authorService.getWordDetails(
            this.callUuid,
            this.personaUuid
          );
        })
      )
      .subscribe({
        next: (wordDetails: IGetWordDetailsResponse) => {
          words.forEach((word) => {
            const itemId = word.definitionUuid || word.key?.trim() || '';
            this.apiErrors.delete(itemId);
          });
          this.updateFormWithWordDetails(wordDetails, false);
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
        },
        error: (error: any) => {
          console.error(
            'Error creating keywords or fetching word details:',
            error
          );
          if (error?.status === this.DUPLICATE_WORD_STATUS) {
            this.handleDuplicateError(words);
          }
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
        },
      });
  }

  private updateKeyword(words: IWordDefinition[]): void {
    if (!this.callUuid || words.length === 0) {
      return;
    }

    const payload = this.transformWordsToRequest(words, true);

    if (payload.wordTypeEntries.length === 0) {
      console.warn('No valid word type entries to update');
      return;
    }

    this.apiRequestCount.update((count) => count + 1);
    this.authorService.updateWords(payload).subscribe({
      next: (response: any) => {
        words.forEach((word) => {
          const itemId = word.definitionUuid || word.key?.trim() || '';
          const error = this.apiErrors.get(itemId);
          if (error && error !== this.ERROR_DUPLICATE_WORD) {
            this.apiErrors.delete(itemId);
          }
        });
        this.cleanTranscriptWordsListControl.updateValueAndValidity();
        this.apiRequestCount.update((count) => Math.max(0, count - 1));
      },
      error: (error: any) => {
        console.error('Error updating keywords:', error);
        if (error?.status === this.DUPLICATE_WORD_STATUS) {
          this.handleDuplicateError(words);
        }
        this.apiRequestCount.update((count) => Math.max(0, count - 1));
      },
    });
  }

  private processCleanTranscriptWordsForAPI(): void {
    if (!this.cleanTranscriptWordsFormArray) return;

    const cleanTranscriptWords =
      this.cleanTranscriptWordsFormArray.getRawValue() as IWordDefinition[];

    const validWords = cleanTranscriptWords.filter(
      (word) => word.key && word.key.trim().length > 0
    );

    if (validWords.length === 0) {
      return;
    }

    const wordsToUpdate: IWordDefinition[] = [];
    const wordsToCreate: IWordDefinition[] = [];
    const processedUuids = new Set<string>();
    const processedKeys = new Set<string>();

    validWords.forEach((word) => {
      const trimmedKey = word.key.trim();

      if (word.definitionUuid && word.definitionUuid.trim().length > 0) {
        if (processedUuids.has(word.definitionUuid)) {
          wordsToCreate.push({ ...word, definitionUuid: '' });
          return;
        }
        wordsToUpdate.push(word);
        processedUuids.add(word.definitionUuid);
      } else {
        if (processedKeys.has(trimmedKey)) {
          return;
        }

        wordsToCreate.push(word);
        processedKeys.add(trimmedKey);
      }
    });

    if (wordsToUpdate.length > 0) {
      this.updateKeyword(wordsToUpdate);
    }

    if (wordsToCreate.length > 0) {
      this.createKeyword(wordsToCreate);
    }
  }

  onResetToProductDefault(): void {
    if (this.callUuid) {
      const wordType: CallWordType = 'clean_transcript_words';
      this.apiRequestCount.update((count) => count + 1);
      this.authorService
        .resetCallKeywords(this.callUuid, wordType)
        .pipe(
          switchMap(() => {
            return this.authorService.getWordDetails(
              this.callUuid,
              this.personaUuid
            );
          })
        )
        .subscribe({
          next: (wordDetails: IGetWordDetailsResponse) => {
            this.updateFormWithWordDetails(wordDetails, true);
            this.apiRequestCount.update((count) => Math.max(0, count - 1));
          },
          error: (error: any) => {
            console.error(
              'Error resetting to product default or fetching word details:',
              error
            );
            this.apiRequestCount.update((count) => Math.max(0, count - 1));
          },
        });
    }
  }

  saveAsProductDefault(): void {
    this.saveAsProductDefaultModalOpen = true;
  }

  onSaveAsProductDefaultConfirm(): void {
    if (!this.callUuid || !this.moduleUuid) {
      console.error('Missing callUuid or moduleUuid');
      return;
    }

    this.networkRequestInProgress = true;
    this.apiRequestCount.update((count) => count + 1);

    this.authorService
      .saveAsProductDefault({
        moduleUuid: this.moduleUuid,
        wordType: 'clean_transcript_words' as CallWordType,
        callUuid: this.callUuid,
        personaUuid: this.personaUuid,
      })
      .pipe(
        switchMap(() => {
          return this.authorService.getWordDetails(
            this.callUuid,
            this.personaUuid
          );
        })
      )
      .subscribe({
        next: (wordDetails: IGetWordDetailsResponse) => {
          console.log(
            'Save as product default successful, updating form with word details'
          );
          this.updateFormWithWordDetails(wordDetails, true);
          this.networkRequestInProgress = false;
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
          this.saveAsProductDefaultModalOpen = false;
        },
        error: (error: any) => {
          console.error(
            'Error saving as product default or fetching word details:',
            error
          );
          this.networkRequestInProgress = false;
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
        },
      });
  }

  onSaveAsProductDefaultModalClose(): void {
    this.saveAsProductDefaultModalOpen = false;
  }

  onDetachFromProductDefault(): void {
    if (this.callUuid && this.moduleUuid) {
      this.apiRequestCount.update((count) => count + 1);
      this.authorService
        .cloneModuleKeywords({
          callUuid: this.callUuid,
          moduleUuid: this.moduleUuid,
          wordType: 'clean_transcript_words' as CallWordType,
        })
        .pipe(
          switchMap(() => {
            return this.authorService.getWordDetails(
              this.callUuid,
              this.personaUuid
            );
          })
        )
        .subscribe({
          next: (wordDetails: IGetWordDetailsResponse) => {
            console.log(
              'Detach from product default successful, updating form with word details'
            );
            this.updateFormWithWordDetails(wordDetails, true);
            this.apiRequestCount.update((count) => Math.max(0, count - 1));
          },
          error: (error: any) => {
            console.error(
              'Error detaching from product default or fetching word details:',
              error
            );
            this.apiRequestCount.update((count) => Math.max(0, count - 1));
          },
        });
    }
  }

  private loadPersonaUuid(): void {
    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form: FormGroup) => {
        const generalSection = form.get(
          SimulationConstants.GENERAL_SECTION
        ) as FormGroup;
        const callPersonaGroup = generalSection?.get(
          SimulationConstants.CALL_PERSONA_SECTION
        ) as FormGroup;
        const personaUuidControl = callPersonaGroup?.get('uuid');
        if (personaUuidControl) {
          this.personaUuid = personaUuidControl.value || '';
        }
      });
  }

  private loadModuleUuid(): void {
    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form: FormGroup) => {
        const generalSection = form.get(
          SimulationConstants.GENERAL_SECTION
        ) as FormGroup;
        const moduleUuidControl = generalSection?.get('moduleUuid');
        if (moduleUuidControl) {
          this.moduleUuid = moduleUuidControl.value || '';
        }
      });
  }

  onWordRemoved(removedWord: WordItem): void {
    if (!this.cleanTranscriptWordsFormArray || !removedWord) {
      return;
    }

    if (!removedWord.uuid || removedWord.uuid.trim().length === 0) {
      return;
    }

    const currentWords =
      this.cleanTranscriptWordsFormArray.getRawValue() as IWordDefinition[];
    const wordToDelete = currentWords.find(
      (word) => word.definitionUuid === removedWord.uuid
    );

    if (!wordToDelete) {
      return;
    }

    // Use scopeUuid from FormArray if available, otherwise calculate it
    const scopeUuid =
      wordToDelete.scopeUuid ||
      this.getScopeUuid(
        wordToDelete.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE
      );

    if (!scopeUuid || scopeUuid.trim().length === 0) {
      return;
    }

    // Call delete API
    this.deleteWords([wordToDelete.definitionUuid], scopeUuid);
  }

  private deleteWords(uuids: string[], scopeValue: string): void {
    if (uuids.length === 0 || !scopeValue) {
      return;
    }

    const currentWordItems = this.cleanTranscriptWordsListControl?.value || [];
    const remainingWordItems = currentWordItems.filter(
      (item) => !item.uuid || !uuids.includes(item.uuid)
    );
    const willHaveNoWords = remainingWordItems.length === 0;

    this.apiRequestCount.update((count) => count + 1);
    this.authorService
      .deleteWords({
        uuids,
        scopeValue,
      })
      .pipe(
        switchMap(() => {
          if (willHaveNoWords) {
            return this.authorService.getWordDetails(
              this.callUuid,
              this.personaUuid
            );
          }
          return of([]);
        })
      )
      .subscribe({
        next: (wordDetails: IGetWordDetailsResponse | any[]) => {
          if (willHaveNoWords && Array.isArray(wordDetails)) {
            this.updateFormWithWordDetails(wordDetails, true);
          }
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
        },
        error: (error: any) => {
          console.error('Error deleting words:', error);
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
        },
      });
  }

  private handleDuplicateError(words: IWordDefinition[]): void {
    const allFormWords =
      (this.cleanTranscriptWordsFormArray?.getRawValue() as IWordDefinition[]) ||
      [];
    const duplicateWordsSet = this.detectDuplicateWords(words, allFormWords);

    if (duplicateWordsSet.size > 0) {
      duplicateWordsSet.forEach((word) => this.duplicateWords.add(word));
      this.setErrorsOnDuplicateWords(duplicateWordsSet, allFormWords, words);
    }

    this.cleanTranscriptWordsListControl.updateValueAndValidity();
  }

  private setErrorsOnDuplicateWords(
    duplicateWordsSet: Set<string>,
    allFormWords: IWordDefinition[],
    requestWords: IWordDefinition[]
  ): void {
    const allWordsToCheck = [...allFormWords, ...requestWords];

    duplicateWordsSet.forEach((duplicateWord) => {
      allWordsToCheck.forEach((word) => {
        const trimmedKey = word.key?.trim() || '';
        if (trimmedKey === duplicateWord) {
          const itemId = word.definitionUuid || trimmedKey;
          this.apiErrors.set(itemId, this.ERROR_DUPLICATE_WORD);
        }
      });
    });
  }

  private detectDuplicateWords(
    words: IWordDefinition[],
    allFormWords?: IWordDefinition[]
  ): Set<string> {
    const duplicates = new Set<string>();
    const formWords =
      allFormWords && allFormWords !== words
        ? allFormWords
        : (this.cleanTranscriptWordsFormArray?.getRawValue() as IWordDefinition[]) ||
          [];

    const wordCounts = new Map<string, number>();

    formWords.forEach((w) => {
      const trimmedKey = w.key?.trim() || '';
      if (trimmedKey) {
        wordCounts.set(trimmedKey, (wordCounts.get(trimmedKey) || 0) + 1);
      }
    });

    wordCounts.forEach((count, word) => {
      if (count > 1) {
        duplicates.add(word);
      }
    });

    return duplicates;
  }

  private validateAndUpdateErrors(): void {
    const currentWords =
      (this.cleanTranscriptWordsFormArray?.getRawValue() as IWordDefinition[]) ||
      [];
    const controlWords = this.cleanTranscriptWordsListControl?.value || [];

    const allWords: IWordDefinition[] = [...currentWords];
    controlWords.forEach((item) => {
      const trimmedValue = item.value?.trim() || '';
      if (trimmedValue) {
        const existsInFormArray = currentWords.some(
          (w) => w.key?.trim() === trimmedValue
        );
        if (!existsInFormArray) {
          allWords.push({
            key: trimmedValue,
            value: null,
            definitionUuid: item.uuid || '',
          } as IWordDefinition);
        }
      }
    });

    const duplicateWordsSet = this.detectDuplicateWords(allWords);

    const currentWordValues = new Set<string>();
    const currentItemIds = new Map<string, string>();

    currentWords.forEach((word) => {
      const trimmedKey = word.key?.trim() || '';
      if (trimmedKey) {
        currentWordValues.add(trimmedKey);
        const itemId = word.definitionUuid || trimmedKey;
        currentItemIds.set(itemId, trimmedKey);
      }
    });

    controlWords.forEach((item) => {
      const trimmedValue = item.value?.trim() || '';
      if (trimmedValue) {
        const itemId = item.uuid || trimmedValue;
        if (!currentItemIds.has(itemId)) {
          currentItemIds.set(itemId, trimmedValue);
        }
      }
    });

    const itemsToClear: string[] = [];
    this.apiErrors.forEach((error, itemId) => {
      const wordValue = currentItemIds.get(itemId);
      const exists = wordValue !== undefined;
      const isDuplicate = wordValue ? duplicateWordsSet.has(wordValue) : false;

      if (!exists) {
        itemsToClear.push(itemId);
      } else if (!isDuplicate && error === this.ERROR_DUPLICATE_WORD) {
        itemsToClear.push(itemId);
      }
    });

    itemsToClear.forEach((itemId) => this.apiErrors.delete(itemId));

    this.duplicateWords.clear();
    duplicateWordsSet.forEach((word) => this.duplicateWords.add(word));
  }

  private clearErrorsForModifiedWords(currentWordItems: WordItem[]): void {
    const currentItemIds = new Set<string>();

    currentWordItems.forEach((item) => {
      const trimmedValue = item.value?.trim() || '';
      if (trimmedValue) {
        const itemId = item.uuid || trimmedValue;
        currentItemIds.add(itemId);
      }
    });

    const itemsToClear: string[] = [];
    this.apiErrors.forEach((error, itemId) => {
      if (error === this.ERROR_DUPLICATE_WORD) {
        return;
      }
      if (!currentItemIds.has(itemId)) {
        itemsToClear.push(itemId);
      }
    });

    itemsToClear.forEach((itemId) => this.apiErrors.delete(itemId));
  }

  ngOnDestroy(): void {
    if (this.cleanTranscriptWordsSubscription) {
      this.cleanTranscriptWordsSubscription.unsubscribe();
    }
    if (this.cleanTranscriptWordsListControlSubscription) {
      this.cleanTranscriptWordsListControlSubscription.unsubscribe();
    }
  }
}
